<template>
  <div class="document-questions-container" :class="{ 'editing-phase': currentPhase === 'editing' }">
    <!-- AI Assistant Dialog -->
    <AIAssistant
      v-model="showAIAssistant"
      :currentDocument="document"
      @template-selected="handleTemplateSelection"
    />

    <!-- Selected Template Display - always show at top when available -->
    <div v-if="selectedTemplate" class="template-header">
      <div class="template-header-content">
        <div class="template-info-header">
          <div class="template-icon">
            <v-icon size="24">mdi-file-document-outline</v-icon>
          </div>
          <div class="template-details-header">
            <span class="template-name-header">{{ currentTemplateName }}</span>
            <div class="document-name-section">
              <v-text-field
                v-model="documentName"
                :placeholder="$t('documentQuestions.documentNamePlaceholder')"
                variant="underlined"
                density="compact"
                hide-details
                class="document-name-input"
                @blur="handleDocumentNameUpdate"
                @keydown.enter="handleDocumentNameUpdate"
              />
            </div>
          </div>
        </div>
        <button
          @click="goBackToTemplateSelection"
          class="back-button-header"
          :title="$t('documentQuestions.backToTemplates')"
        >
          <v-icon size="18">mdi-chevron-left</v-icon>
          {{ $t('documentQuestions.changeTemplate') }}
        </button>
      </div>
    </div>

    <div class="steps-header">
      <!-- PLIK BUTTON - DESKTOP -->
      <div v-if="documentId && currentPhase === 'editing'" class="file-menu-wrapper desktop-only">
        <DocumentNavigator
          :document="document || { id: documentId, name: documentMeta?.title || 'Document' }"
          :hasWizardCompleted="isDocumentFullyCompleted"
          :buttons="['file']"
          @updateDocument="handleUpdateDocument"
          @deletedDocument="handleDeletedDocument"
          @showHistoryModal="showHistoryModal = true"
          @showShareHistoryDialog="showHistoryModal = true"
          @toggleAiAssistant="showAIAssistant = !showAIAssistant"
        />
      </div>


      <div class="steps-container">
        <div class="step-item active">
          <div class="step-number active">1</div>
          <span class="step-text">{{ $t('welcome.step1_title') }}</span>
        </div>

        <div class="step-connector"></div>

        <div class="step-item" :class="{ active: currentPhase === 'questions' || currentPhase === 'editing' }">
          <div class="step-number" :class="{
            active: currentPhase === 'questions' || currentPhase === 'editing',
            pulse: currentPhase === 'questions'
          }">2</div>
          <span class="step-text">{{ $t('welcome.step2_title') }}</span>
        </div>

        <div class="step-connector"></div>

        <div class="step-item" :class="{ active: currentPhase === 'editing' }">
          <div class="step-number" :class="{
            active: currentPhase === 'editing',
            pulse: currentPhase === 'editing'
          }">3</div>
          <span class="step-text">{{ $t('welcome.step3_title') }}</span>
        </div>

        <!-- PLIK BUTTON FOR MOBILE/TABLET - positioned in header -->
        <v-menu v-model="showFileMenu" :close-on-content-click="true" location="bottom end" offset="8">
          <template v-slot:activator="{ props }">
            <button
              v-if="documentId && currentPhase === 'editing'"
              class="mobile-plik-button-inline"
              v-bind="props"
            >
              {{ $t('documentQuestions.file') }}
            </button>
          </template>

          <v-card min-width="180">
            <v-list>
              <v-list-item @click="handleSaveDocument">
                <template v-slot:prepend>
                  <v-icon size="20">mdi-content-save</v-icon>
                </template>
                <v-list-item-title>{{ $t('common.save') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="handleDownloadDocument" :disabled="!isDocumentFullyCompleted">
                <template v-slot:prepend>
                  <v-icon size="20">{{ !isDocumentFullyCompleted ? 'mdi-lock' : 'mdi-download' }}</v-icon>
                </template>
                <v-list-item-title>{{ $t('common.download') }}</v-list-item-title>
              </v-list-item>
              <v-list-item @click="handlePrintDocument" :disabled="!isDocumentFullyCompleted">
                <template v-slot:prepend>
                  <v-icon size="20">{{ !isDocumentFullyCompleted ? 'mdi-lock' : 'mdi-printer' }}</v-icon>
                </template>
                <v-list-item-title>{{ $t('common.print') }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
      </div>
    </div>

    <div class="content-wrapper">
      <!-- Loading skeleton - shows during initial phase detection -->
      <div v-if="currentPhase === 'loading'" class="loading-phase-container">
        <!-- Questions skeleton - for new documents -->
        <div v-if="loadingType === 'questions'" class="loading-container">
          <div class="skeleton-card">
            <!-- Progress bar skeleton -->
            <div class="skeleton-progress">
              <div class="skeleton-counter"></div>
              <div class="skeleton-bar">
                <div class="skeleton-bar-fill"></div>
              </div>
            </div>

            <!-- Question header skeleton -->
            <div class="skeleton-header">
              <div class="skeleton-icon"></div>
              <div class="skeleton-content">
                <div class="skeleton-title"></div>
                <div class="skeleton-subtitle"></div>
              </div>
            </div>

            <!-- Question text skeleton -->
            <div class="skeleton-question">
              <div class="skeleton-number"></div>
              <div class="skeleton-text">
                <div class="skeleton-line long"></div>
                <div class="skeleton-line medium"></div>
                <div class="skeleton-line short"></div>
              </div>
            </div>

            <!-- Answer options skeleton -->
            <div class="skeleton-options">
              <div class="skeleton-option">
                <div class="skeleton-radio"></div>
                <div class="skeleton-option-text"></div>
              </div>
              <div class="skeleton-option">
                <div class="skeleton-radio"></div>
                <div class="skeleton-option-text"></div>
              </div>
            </div>

            <!-- Navigation skeleton -->
            <div class="skeleton-navigation">
              <div class="skeleton-nav-button"></div>
              <div class="skeleton-nav-button primary"></div>
            </div>
          </div>
        </div>

        <!-- Document editing skeleton - for refresh/existing documents -->
        <div v-else-if="loadingType === 'document'" class="editing-skeleton">
          <!-- Document preview skeleton -->
          <div class="skeleton-document-preview">
            <div class="skeleton-document-header">
              <div class="skeleton-line title"></div>
              <div class="skeleton-line subtitle"></div>
            </div>
            <div class="skeleton-document-content">
              <div class="skeleton-paragraph">
                <div class="skeleton-line long"></div>
                <div class="skeleton-line long"></div>
                <div class="skeleton-line medium"></div>
              </div>
              <div class="skeleton-paragraph">
                <div class="skeleton-line long"></div>
                <div class="skeleton-line long"></div>
                <div class="skeleton-line short"></div>
              </div>
              <div class="skeleton-paragraph">
                <div class="skeleton-line long"></div>
                <div class="skeleton-line medium"></div>
              </div>
            </div>
          </div>
          <!-- Variables panel skeleton -->
          <div class="skeleton-variables-panel">
            <div class="skeleton-panel-header">
              <div class="skeleton-line header-title"></div>
              <div class="skeleton-line header-subtitle"></div>
            </div>
            <div class="skeleton-panel-content">
              <div class="skeleton-input-group">
                <div class="skeleton-label"></div>
                <div class="skeleton-input"></div>
              </div>
              <div class="skeleton-input-group">
                <div class="skeleton-label"></div>
                <div class="skeleton-input"></div>
              </div>
              <div class="skeleton-input-group">
                <div class="skeleton-label"></div>
                <div class="skeleton-input"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Description text only - without bold title -->
      <div v-if="currentPhase === 'questions'" class="intro-section">
        <p class="description">{{ $t('documentQuestions.description') }}</p>
      </div>

      <!-- Questions Section - only show when in questions phase -->
      <div v-if="currentPhase === 'questions'" class="questions-section">
        <div v-if="isLoading" class="loading-container">
          <div class="skeleton-card">
            <!-- Progress bar skeleton -->
            <div class="skeleton-progress">
              <div class="skeleton-counter"></div>
              <div class="skeleton-bar">
                <div class="skeleton-bar-fill"></div>
              </div>
            </div>

            <!-- Question header skeleton -->
            <div class="skeleton-header">
              <div class="skeleton-icon"></div>
              <div class="skeleton-content">
                <div class="skeleton-title"></div>
                <div class="skeleton-subtitle"></div>
              </div>
            </div>

            <!-- Question text skeleton -->
            <div class="skeleton-question">
              <div class="skeleton-number"></div>
              <div class="skeleton-text">
                <div class="skeleton-line long"></div>
                <div class="skeleton-line medium"></div>
                <div class="skeleton-line short"></div>
              </div>
            </div>

            <!-- Answer options skeleton -->
            <div class="skeleton-options">
              <div class="skeleton-option">
                <div class="skeleton-radio"></div>
                <div class="skeleton-option-text"></div>
              </div>
              <div class="skeleton-option">
                <div class="skeleton-radio"></div>
                <div class="skeleton-option-text"></div>
              </div>
            </div>

            <!-- Navigation skeleton -->
            <div class="skeleton-navigation">
              <div class="skeleton-nav-button"></div>
              <div class="skeleton-nav-button primary"></div>
            </div>
          </div>
        </div>

        <div v-else-if="!documentId" class="error-container">
          <v-icon size="48" color="error">mdi-alert-circle-outline</v-icon>
          <p>{{ $t('documentQuestions.noTemplate') }}</p>
          <v-btn
            @click="goBackToTemplateSelection"
            variant="outlined"
            color="primary"
          >
            {{ $t('documentQuestions.backToTemplates') }}
          </v-btn>
        </div>

        <div v-else-if="documentMeta && wizardSteps.length > 0" class="wizard-container">
          <DocumentQuestionsWizard
            @complete-wizard="handleCompleteWizard"
            :isAiAssistantOpen="showAIAssistant"
          />
        </div>

        <div v-else-if="!isLoading && documentMeta && wizardSteps.length === 0" class="no-questions">
          <v-icon size="48" color="success">mdi-check-circle-outline</v-icon>
          <p>{{ $t('documentQuestions.noQuestionsNeeded') }}</p>
          <v-btn
            @click="proceedToDataEntry"
            variant="flat"
            color="primary"
            class="proceed-button"
          >
            {{ $t('documentQuestions.proceedToData') }}
          </v-btn>
        </div>
      </div>

      <!-- Payment Required Section -->
      <div v-if="requiresPayment && !isPaymentDialogOpen" class="payment-required-section">
        <div class="payment-required-container">
          <!-- Header with icon and status -->
          <div class="payment-header">
            <div class="payment-status-icon">
              <v-icon size="20" color="white">mdi-lock</v-icon>
            </div>
            <div class="payment-header-text">
              <h3>{{ $t('documentQuestions.premiumDocument') }}</h3>
              <p>{{ $t('documentQuestions.paymentRequired') }}</p>
            </div>
          </div>

          <!-- Main content -->
          <div class="payment-content">
            <div class="payment-illustration">
              <div class="document-icon">
                <v-icon size="32" color="#64748B">mdi-file-document-outline</v-icon>
              </div>
              <div class="lock-overlay">
                <v-icon size="24" color="#0023EF">mdi-lock</v-icon>
              </div>
            </div>

            <div class="payment-info">
              <h4>{{ currentTemplateName }}</h4>
              <p>{{ $t('documentQuestions.documentAvailableDescription') }}</p>
            </div>
          </div>

          <!-- Actions -->
          <div class="payment-actions">
            <button
              @click="isPaymentDialogOpen = true"
              class="payment-button primary"
            >
              <v-icon size="18">mdi-credit-card</v-icon>
              {{ $t('documentQuestions.choosePaymentPlan') }}
            </button>
            <button
              @click="goBackToTemplateSelection"
              class="payment-button secondary"
            >
              <v-icon size="18">mdi-arrow-left</v-icon>
              {{ $t('documentQuestions.changeTemplate') }}
            </button>
          </div>
        </div>
      </div>

      <!-- Document Editing Phase -->
      <div v-if="currentPhase === 'editing' && document && !requiresPayment" class="document-editing-section">
        <div class="editing-content">
          <!-- Document Preview - now on the left and bigger -->
          <div class="document-preview-section">
            <Wysiwyg
              v-if="document"
              :document="document"
              :isWizardCompleted="isWizardCompleted"
              :isWizardVariablesCompleted="isWizardVariablesCompleted"
              :editorEditable="isDocumentFullyCompleted"
            />
          </div>

          <!-- Variable Inputs for filling document data - now on the right -->
          <div class="variable-inputs-section">
            <div class="panel-header">
              <div class="header-content">
                <div class="header-text">
                  <h3>Uzupełnij dane</h3>
                  <p>Wypełnij poniższe pola, aby spersonalizować dokument</p>
                </div>
              </div>
            </div>
            <div class="panel-content">
              {{ console.log('🔧 Rendering variables section. isWizardVariablesCompleted:', isWizardVariablesCompleted) || '' }}
              {{ console.log('🔧 Should show WizardVariables:', !isWizardVariablesCompleted) || '' }}
              {{ console.log('🔧 documentMeta:', documentMeta) || '' }}
              {{ console.log('🔧 document:', document) || '' }}
              <div v-if="!document || !document.id" class="loading-variables">
                <v-progress-circular
                  indeterminate
                  color="primary"
                  size="40"
                  class="mb-4"
                ></v-progress-circular>
                <p>Ładowanie danych dokumentu...</p>
              </div>
              <WizardVariables
                v-else-if="!isWizardVariablesCompleted && document && document.id"
                @complete-wizard="handleCompleteVariablesWizard"
                @next="handleSaveEditableVariable"
                :currentDocument="document"
                :meta="documentMeta"
              />
              <div v-else-if="isWizardVariablesCompleted" class="variables-completed">
                <div class="completion-icon">
                  <v-icon size="64" color="#10B981">mdi-check-circle</v-icon>
                </div>
                <h4>Świetnie!</h4>
                <p>Wszystkie dane zostały uzupełnione. Twój dokument jest gotowy do pobrania, udostępnienia lub podpisu.</p>

                <!-- Action buttons row -->
                <div class="action-buttons-row">
                  <div class="action-button" @click="handleDownloadDocument">
                    <div class="action-button-icon">
                      <v-icon size="24">mdi-download</v-icon>
                    </div>
                    <span>{{ $t('common.download') }}</span>
                  </div>
                  <v-menu location="bottom" offset="8">
                    <template v-slot:activator="{ props }">
                      <div class="action-button" v-bind="props">
                        <div class="action-button-icon">
                          <v-icon size="24">mdi-share-variant</v-icon>
                        </div>
                        <span>{{ $t('common.share') }}</span>
                      </div>
                    </template>
                    <v-list>
                      <v-list-item @click="copyLinkToClipboard">
                        <template v-slot:prepend>
                          <v-icon size="20">mdi-link</v-icon>
                        </template>
                        <v-list-item-title>Skopiuj link</v-list-item-title>
                      </v-list-item>
                      <v-list-item @click="showShareViaEmailDialog = true">
                        <template v-slot:prepend>
                          <v-icon size="20">mdi-email</v-icon>
                        </template>
                        <v-list-item-title>{{ $t('creator.share_email') }}</v-list-item-title>
                      </v-list-item>
                    </v-list>
                  </v-menu>
                  <div class="action-button" @click="handlePrintDocument">
                    <div class="action-button-icon">
                      <v-icon size="24">mdi-printer</v-icon>
                    </div>
                    <span>{{ $t('common.print') }}</span>
                  </div>
                  <div class="action-button" @click="handleSignDocument">
                    <div class="action-button-icon">
                      <v-icon size="24">mdi-pen</v-icon>
                    </div>
                    <span>{{ $t('common.sign') }}</span>
                  </div>
                </div>

                <div class="completion-actions">
                  <v-btn
                    variant="flat"
                    size="large"
                    class="single-action-btn"
                    prepend-icon="mdi-file-eye"
                    @click="viewDocuments"
                  >
                    Moje dokumenty
                  </v-btn>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Dialog -->
    <SubscriptionOrSinglePaymentRequiredDialog
      v-if="requiresPayment || isPaymentDialogOpen"
      :is-dialog-open="requiresPayment || isPaymentDialogOpen"
      :show-close-button="true"
      :template-id="document?.template_id"
      :document-id="documentId"
      @payment-completed="handlePaymentCompleted"
      @close-dialog="isPaymentDialogOpen = false"
    />

    <!-- Share Document Via Email Dialog -->
    <ShareDocumentViaEmailDialog
      :is-dialog-open="showShareViaEmailDialog"
      @close-dialog="showShareViaEmailDialog = false"
      :document="document"
    />

    <!-- Change Template Confirmation Dialog -->
    <v-dialog
      v-model="showChangeTemplateDialog"
      max-width="500"
      persistent
    >
      <v-card class="change-template-dialog">
        <v-card-title class="dialog-title">
          {{ $t('documentQuestions.changeTemplateDialog.title') }}
        </v-card-title>

        <v-card-text class="dialog-content">
          {{ $t('documentQuestions.changeTemplateDialog.message') }}
        </v-card-text>

        <v-card-actions class="dialog-actions">
          <v-btn
            variant="outlined"
            color="grey"
            @click="showChangeTemplateDialog = false"
          >
            {{ $t('documentQuestions.changeTemplateDialog.cancel') }}
          </v-btn>
          <v-btn
            variant="flat"
            color="primary"
            @click="confirmChangeTemplate"
          >
            {{ $t('documentQuestions.changeTemplateDialog.continue') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- History Modal -->
    <EditorHistoryModal
      v-if="document"
      :isHistoryOpen="showHistoryModal"
      :documentId="document.id"
      @close="showHistoryModal = false"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, watch, provide, nextTick } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useStore } from 'vuex';
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import useGTM from '@/utils/gtm';
import parseXMLToJSON from '@/utils/xmlParser/parseXMLToJSON';
import AIAssistant from '@/components/AIAssistant.vue';
import DocumentQuestionsWizard from '@/components/documents/wizard/DocumentQuestionsWizard.vue';
import WizardVariables from '@/components/documents/wizard/WizardVariables.vue';
import Wysiwyg from '@/components/documents/creator/Wysiwyg.vue';
import DocumentNavigator from '@/components/documents/navigator/DocumentNavigator.vue';
import SubscriptionOrSinglePaymentRequiredDialog from '@/components/dialogs/SubscriptionOrSinglePaymentRequiredDialog.vue';
import ShareDocumentViaEmailDialog from '@/components/documents/navigator/partials/ShareDocumentViaEmailDialog.vue';
import EditorHistoryModal from '@/components/ProseMirror/EditorHistoryModal.vue';
import useDocusign from '@/plugins/docusign';
import printJS from 'print-js';

const router = useRouter();
const route = useRoute();
const store = useStore();
const toast = useToast();
const { t } = useI18n();
const { uploadDocumentToDocusign } = useDocusign();

const selectedTemplate = ref(null);
const showAIAssistant = ref(false);
const isLoading = ref(false);
const documentId = ref(null);
const documentMeta = ref(null);
const documentName = ref('');

// New state for document editing phase
// FIXED: Use computed from Vuex store instead of local ref
const isWizardCompleted = computed(() => store.state.editor.wizard.isCompleted);
const isWizardVariablesCompleted = ref(false);
const isPaymentDialogOpen = ref(false);
const showChangeTemplateDialog = ref(false);
const isInitialLoad = ref(true); // Track if this is initial page load
provide("isPaymentDialogOpen", isPaymentDialogOpen);
const document = ref(null);
const currentPhase = ref('loading'); // 'loading', 'questions', 'payment', 'editing'
const loadingType = ref('questions'); // 'questions' or 'document' - determines which skeleton to show

// State for DocumentNavigator
const showHistoryModal = ref(false);
const showFileMenu = ref(false);
const showShareViaEmailDialog = ref(false);


// Document is truly complete when both wizard questions AND variables are done
const isDocumentFullyCompleted = computed(() => {
  return isWizardCompleted.value && isWizardVariablesCompleted.value;
});

// Watch currentPhase changes for debugging
watch(currentPhase, (newPhase, oldPhase) => {
  console.log(`🔄 Phase changed: ${oldPhase} → ${newPhase}`);
});

// Initialize document name when document data is loaded
watch([document, documentMeta], ([newDocument, newDocumentMeta]) => {
  if (!documentName.value && (newDocument?.name || newDocumentMeta?.title)) {
    documentName.value = newDocument?.name || newDocumentMeta?.title || currentTemplateName.value || '';
  }
}, { immediate: true });

// Get selected template from store or route
const getSelectedTemplate = () => {
  const fromStore = store.getters['shared/getSelectedTemplate'];
  const templateIdFromQuery = route.query.template;

  console.log('Template from store:', fromStore);
  console.log('Template ID from query:', templateIdFromQuery);

  if (fromStore) {
    selectedTemplate.value = fromStore;
    return fromStore;
  } else if (templateIdFromQuery) {
    // If we have template ID but no template object, create a minimal template object
    const template = { id: parseInt(templateIdFromQuery), name: 'Selected Template' };
    selectedTemplate.value = template;
    return template;
  }
  return null;
};

// Computed properties from Vuex store
const wizardSteps = computed(() => store.state.editor.wizard.steps || []);

// Document editing computed properties
const userHasSubscription = computed(() => store.state.auth.user.hasValidSubscription);
const requiresPayment = computed(() =>
  !userHasSubscription.value && document.value && !document.value.is_paid
);

// Template name computed property
const currentTemplateName = computed(() => {
  // First try to get from document meta
  if (documentMeta.value && documentMeta.value.title) {
    return documentMeta.value.title;
  }

  // Then try selected template
  if (selectedTemplate.value) {
    return getTemplateName(selectedTemplate.value);
  }

  return t('documentQuestions.selectedTemplate');
});

// Progress tracking computed properties
const totalFieldsCount = computed(() => {
  if (!documentMeta.value || !documentMeta.value.variables) return 0;
  return documentMeta.value.variables.length;
});

const completedFieldsCount = computed(() => {
  if (!documentMeta.value || !documentMeta.value.variables) return 0;
  return documentMeta.value.variables.filter(variable =>
    variable.value && variable.value.trim() !== ''
  ).length;
});

const progressPercentage = computed(() => {
  if (totalFieldsCount.value === 0) return 0;
  return Math.round((completedFieldsCount.value / totalFieldsCount.value) * 100);
});

const parseVariables = (variables) => {
  const items = variables.map((variable) => {
    if (!variable.options) return variable;

    return {
      ...variable,
      options: variable.options.split(";"),
      max_options: variable.max_options,
      list_type: variable.list_type,
    };
  });

  return items || [];
};

const createDocumentFromTemplate = async (templateId) => {
  try {
    console.log('Creating document with template ID:', templateId);

    const response = await axios.post("api/documents", {
      template_id: templateId,
    });

    console.log('Document creation response:', response.data);

    const docId = response.data.data.id;
    documentId.value = docId;

    // Update store with document info
    store.commit("editor/updateDocumentId", docId);
    store.commit("chat/updateSessionId", docId);

    return docId;
  } catch (error) {
    console.error('Error creating document:', error);
    console.error('Error response:', error.response?.data);
    throw error;
  }
};

const getWizardData = async (docId) => {
  try {
    // If we're already in editing phase or have already loaded meta, skip redundant fetching
    if (currentPhase.value === 'editing' && documentMeta.value) {
      console.log('🔧 Already in editing phase with meta loaded, skipping getWizardData');
      store.commit("editor/completeWizard");
      return;
    }

    // Only fetch meta if we don't have it already
    if (!documentMeta.value) {
      const response = await axios.get(`api/documents/${docId}/meta`);
      documentMeta.value = response.data.data;
    }

    const { are_variables_set } = documentMeta.value;
    const { is_completed, steps } = documentMeta.value.wizard;
    const { variables, title } = documentMeta.value;

    if (is_completed) {
      store.commit("editor/completeWizard");
    }

    if (!is_completed) {
      isWizardVariablesCompleted.value = false;
    }

    // IMPORTANT: Always load wizard answers, even if completed
    // These answers are needed to determine which variables should be displayed
    // Clear any existing wizard answers first
    store.commit("editor/setWizardAnswers", []);

    // Setup wizard data in store - Load saved answers from backend
    const wizardAnswers = steps.reduce((answers, step) => {
      step.subSteps.forEach((subStep) => {
        subStep.questions.forEach((question) => {
          // Load saved answer from backend if it exists, otherwise set to null
          const savedAnswer = question.answer || null;
          answers.push({
            stepId: step.id,
            questionId: question.id,
            answer: savedAnswer,
          });
        });
      });
      return answers;
    }, []);

    store.commit("editor/setWizardSteps", steps);
    store.commit("editor/setAvailableVariables", parseVariables(variables));
    store.commit("editor/updateDocumentTitle", title);
    store.commit("editor/setWizardAnswers", wizardAnswers);

    if (is_completed) {
      store.commit("editor/completeWizard");
    }

    // If wizard is marked as completed by backend, proceed to data entry
    if (is_completed) {
      proceedToDataEntry();
      return;
    }

    if (are_variables_set) {
      store.commit("editor/completeWizardVariables");
      isWizardVariablesCompleted.value = true;
    }

  } catch (error) {
    console.error('Error fetching wizard data:', error);
    throw error;
  }
};

const initializeQuestions = async () => {
  isLoading.value = true;
  currentPhase.value = 'loading'; // Start with loading phase

  try {
    // Check if we have a document ID from query params (for persistence)
    const existingDocId = route.query.documentId;

    if (existingDocId) {
      // We have an existing document, load its data
      console.log('Loading existing document:', existingDocId);
      documentId.value = existingDocId;

      // Set loading type to document since we're loading an existing document
      loadingType.value = 'document';

      // Update store with document info
      store.commit("editor/updateDocumentId", existingDocId);
      store.commit("chat/updateSessionId", existingDocId);

      // First, fetch meta to determine the actual phase
      const response = await axios.get(`api/documents/${existingDocId}/meta`);
      const metaData = response.data.data;
      documentMeta.value = metaData;

      // Check if wizard is already completed
      if (metaData.wizard?.is_completed) {
        console.log('Wizard already completed, loading document for editing');
        store.commit("editor/completeWizard");

        // IMPORTANT: Load wizard data even if completed
        // This loads the wizard answers which are needed for variable conditional logic
        await getWizardData(existingDocId);

        // Load the full document
        await loadDocumentData();

        // Determine if payment is needed
        const userHasSub = store.state.auth.user.hasValidSubscription;
        if (!userHasSub && document.value && !document.value.is_paid) {
          currentPhase.value = 'payment';
          isPaymentDialogOpen.value = true;
        } else {
          currentPhase.value = 'editing';
        }
      } else {
        // Wizard not complete, show questions
        loadingType.value = 'questions'; // Switch to questions skeleton since wizard isn't complete
        currentPhase.value = 'questions';
        await getWizardData(existingDocId);
      }
      return;
    }

    // Creating a new document - set loading type to questions
    loadingType.value = 'questions';

    // Reset all wizard state first
    console.log('Creating new document - resetting editor state');
    store.commit("editor/resetEditor");

    // Also reset local component state (store.commit resetEditor already resets wizard state)
    isWizardVariablesCompleted.value = false;
    document.value = null;
    documentMeta.value = null;

    const template = getSelectedTemplate();

    if (!template) {
      console.log('No template found');
      isLoading.value = false;
      return;
    }

    // Extract template ID - handle different object structures
    let templateId = template.id;
    if (template.template) {
      templateId = template.template.id;
    }
    if (template.value) {
      templateId = template.value;
    }

    console.log('Extracted template ID:', templateId);

    if (!templateId) {
      console.error('No valid template ID found');
      isLoading.value = false;
      return;
    }

    // Create document from template
    const docId = await createDocumentFromTemplate(templateId);

    // Redirect to URL with document ID for persistence
    router.replace({
      name: 'document-questions',
      query: {
        ...route.query,
        documentId: docId
      }
    });

    // Get wizard questions
    await getWizardData(docId);

  } catch (error) {
    console.error('Error initializing questions:', error);
    // On error, default to questions phase
    if (currentPhase.value === 'loading') {
      currentPhase.value = 'questions';
    }
  } finally {
    isLoading.value = false;
    // If still loading, default to questions
    if (currentPhase.value === 'loading') {
      currentPhase.value = 'questions';
    }
  }
};

const handleCompleteWizard = async () => {
  try {
    // Mark wizard as complete
    await axios.patch(`api/documents/${documentId.value}/meta/complete`);

    // FIXED: Update Vuex store FIRST - this will automatically update computed property
    store.commit("editor/completeWizard");
    console.log('🔧 Vuex wizard completion updated');

    // Ensure Vue reactivity has time to propagate
    await nextTick();
    console.log('🔧 Vue reactivity propagated');

    // Load full document data - ENSURE document is set for PLIK button
    await loadDocumentData();

    // Fallback: If document still not loaded, load it directly
    if (!document.value && documentId.value) {
      try {
        const docResponse = await axios.get(`api/documents/${documentId.value}`);
        document.value = docResponse.data.data;
        console.log('Document loaded for PLIK button:', document.value);
      } catch (err) {
        console.error('Error loading document for PLIK:', err);
      }
    }

    // Check if payment is required
    if (requiresPayment.value) {
      currentPhase.value = 'payment';
      isPaymentDialogOpen.value = true;
    } else {
      currentPhase.value = 'editing';
    }

    console.log('🔧 Final state - isWizardCompleted (Vuex):', store.state.editor.wizard.isCompleted);
    console.log('🔧 Final state - isWizardCompleted (computed):', isWizardCompleted.value);
    console.log('🔧 Final state - currentPhase:', currentPhase.value);
    console.log('🔧 Final state - isDocumentFullyCompleted:', isDocumentFullyCompleted.value);
  } catch (error) {
    console.error('Error completing wizard:', error);
    console.error('Error details:', error.response?.data);
  }
};

// Load full document data for editing
let isLoadingDocument = false; // Prevent infinite recursion
const loadDocumentData = async () => {
  if (isLoadingDocument) {
    console.log('🔧 Preventing recursive loadDocumentData call');
    return;
  }
  isLoadingDocument = true;

  try {
    const response = await axios.get(`api/documents/${documentId.value}`);
    document.value = response.data.data;

    // Ensure document has an ID field
    if (document.value && !document.value.id) {
      document.value.id = documentId.value;
    }

    console.log('🔄 Loaded document:', document.value);

    // Load meta data for WizardVariables
    const metaResponse = await axios.get(`api/documents/${documentId.value}/meta`);
    documentMeta.value = metaResponse.data.data;
    console.log('🔄 Loaded document meta:', documentMeta.value);
    console.log('🔄 Wizard completed status:', documentMeta.value.wizard?.is_completed);
    console.log('🔄 Total wizard steps:', documentMeta.value.wizard?.steps?.length);
    console.log('🔄 Wizard steps:', documentMeta.value.wizard?.steps);

    // Only check for unanswered questions if we're not already in the editing phase
    // This prevents the phase from being reset when the component re-renders
    if (currentPhase.value !== 'editing') {
      // TEMPORARY FIX: Check if we actually have unanswered questions despite backend saying "completed"
      const totalSteps = documentMeta.value.wizard?.steps?.length || 0;
      const answeredQuestions = store.state.editor.wizard.answers?.length || 0;

      console.log('🔧 BACKEND WIZARD BUG CHECK:');
      console.log('🔧 Total steps:', totalSteps);
      console.log('🔧 Answered questions:', answeredQuestions);
      console.log('🔧 Current phase:', currentPhase.value);

      if (totalSteps > 1 && answeredQuestions < totalSteps) {
        console.log('🔧 OVERRIDING: Backend says completed but we have unanswered questions!');
        console.log('🔧 Forcing wizard to continue...');

        // Override the completion status
        documentMeta.value.wizard.is_completed = false;
        // Reset wizard state in store
        store.state.editor.wizard.isCompleted = false;
        currentPhase.value = 'questions';

        console.log('🔧 UI override complete - questions should show now');
        console.log('🔧 Current phase after override:', currentPhase.value);
        console.log('🔧 isWizardCompleted after override:', isWizardCompleted.value);

        // Force a Vue update to make sure the UI reflects our changes
        nextTick(() => {
          console.log('🔧 NEXTTICK: Phase is now:', currentPhase.value);
          console.log('🔧 NEXTTICK: isWizardCompleted is now:', isWizardCompleted.value);
        });

        return; // Exit early to show questions
      }
    }

    // Update store
    store.commit('editor/updateContent', document.value.content);
    store.commit('editor/updateDocumentId', documentId.value);
  } catch (error) {
    console.error('Error loading document data:', error);
  } finally {
    isLoadingDocument = false;
  }
};

const proceedToDataEntry = () => {
  // Legacy function - now we handle everything in this component
  // Just trigger the same flow as completing wizard
  handleCompleteWizard();
};

const goBackToTemplateSelection = () => {
  showChangeTemplateDialog.value = true;
};

const confirmChangeTemplate = () => {
  showChangeTemplateDialog.value = false;
  router.push({ name: 'template-selection' });
};

const handleTemplateSelection = (template) => {
  selectedTemplate.value = template;
  showAIAssistant.value = false;
};

// Handle payment completion
const handlePaymentCompleted = () => {
  isPaymentDialogOpen.value = false;

  console.log('🔄 Payment completed, checking wizard status');
  console.log('🔄 isWizardCompleted:', isWizardCompleted.value);

  // Check if we have unanswered questions despite wizard being marked completed
  const totalSteps = documentMeta.value?.wizard?.steps?.length || 0;
  const answeredQuestions = store.state.editor.wizard.answers?.length || 0;

  console.log('🔄 Post-payment wizard check:');
  console.log('🔄 Total steps:', totalSteps);
  console.log('🔄 Answered questions:', answeredQuestions);

  // If we have unanswered questions, force questions phase regardless of completion status
  if (totalSteps > 1 && answeredQuestions < totalSteps) {
    console.log('🔄 FORCING QUESTIONS: Unanswered questions detected after payment');
    currentPhase.value = 'questions';
    // Reset wizard state in store
    store.state.editor.wizard.isCompleted = false;
    return; // Don't call loadDocumentData which might override our phase
  }

  // Only go to editing if wizard is actually completed, otherwise stay in questions
  if (isWizardCompleted.value) {
    currentPhase.value = 'editing';
  } else {
    currentPhase.value = 'questions';
    console.log('🔄 Returning to questions phase - wizard not completed');
  }

  // Refresh document data after payment
  loadDocumentData();
};

// Save document history
const saveHistory = async () => {
  try {
    // Ensure we have a valid document and document ID
    const docId = documentId.value || document.value?.id;
    if (!docId || !document.value?.content) {
      console.warn('Cannot save history: missing document or document ID');
      return;
    }

    const content = parseXMLToJSON(document.value.content);
    await axios.post(`api/documents/${docId}/history`, {
      content: JSON.stringify(content),
      created_at: new Date().toISOString(),
    });
  } catch (error) {
    console.error('Error saving history:', error);
  }
};

// Handle saving editable variables
const handleSaveEditableVariable = async () => {
  await saveHistory();
};

// Handle document name update
const handleDocumentNameUpdate = async () => {
  if (!documentId.value || !documentName.value.trim()) {
    return;
  }

  try {
    await axios.put(`/api/documents/${documentId.value}/name`, {
      name: documentName.value.trim(),
    });

    // Update the document object if it exists
    if (document.value) {
      document.value.name = documentName.value.trim();
    }

    // Update document meta title if it exists
    if (documentMeta.value) {
      documentMeta.value.title = documentName.value.trim();
    }

    // Save history after name update
    await saveHistory();
    toast.success(t("notifications.document_name_updated"));
  } catch (error) {
    console.error('Error updating document name:', error);
    toast.error(t("notifications.document_save_error"));
  }
};

// Handle variables wizard completion
const handleCompleteVariablesWizard = async () => {
  try {
    // Ensure we have a valid document ID
    const docId = documentId.value || document.value?.id;
    if (!docId) {
      console.error('No document ID available');
      toast.error(t('notifications.default_error'));
      return;
    }

    // Mark variables as complete in backend
    await axios.patch(`api/documents/${docId}/meta/variables/complete`);

    isWizardVariablesCompleted.value = true;
    store.commit("editor/completeWizardVariables");

    // Auto-save the document to update its status
    const updatedContent = store.state.editor.content;
    const content = parseXMLToJSON(updatedContent);

    const documentResponse = await axios.post(`api/documents/${docId}?_method=PUT`, {
      name: document.value?.name || "",
      content: JSON.stringify(content),
      template_id: document.value.template_id,
      directory_id: document.value.directory?.id,
    });

    document.value = documentResponse.data.data;

    // Refresh wizard data
    await getWizardData(docId);
    document.value.meta = documentMeta.value;

    // Only show success notification if user actively completed (not on page load)
    if (!isInitialLoad.value) {
      toast.success(t("notifications.wizard_completed"), {
        timeout: 5000,
      });
    }

    // Save history after wizard completion
    await saveHistory();

    // Track analytics event
    useGTM.pushEvent({
      event: "document_finished",
      document_template: document.value.template_id,
    });

    // Removed automatic redirect - let user decide when to leave
  } catch (error) {
    console.error('Error completing variables wizard:', error);
    toast.error(t('notifications.default_error'));
  }
};

// Download document function
const downloadDocument = async () => {
  try {
    const docId = documentId.value || document.value?.id;
    if (!docId) {
      console.error('No document ID available for download');
      return;
    }

    // Use download_url if available (includes document token), otherwise construct URL
    let downloadPath = document.value?.download_url || `api/documents/${docId}/download`;

    // If it's a full URL, extract just the path + query string
    if (downloadPath.startsWith('http://') || downloadPath.startsWith('https://')) {
      try {
        const urlObj = new URL(downloadPath);
        downloadPath = urlObj.pathname + urlObj.search;
      } catch (e) {
        console.warn('Failed to parse download_url, using as-is:', e);
      }
    }

    // Fetch document as blob with authentication token (added by axios interceptor)
    const response = await axios.get(downloadPath, {
      responseType: 'blob',
    });

    // Create file from blob
    const file = new File([response.data], `${document.value?.name || 'document'}.pdf`, {
      type: 'application/pdf',
    });

    // Create download link and trigger download
    const url = URL.createObjectURL(file);
    const link = window.document.createElement('a');
    link.href = url;
    link.setAttribute('download', file.name);
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(t('notifications.document_saved'));
  } catch (error) {
    console.error('Error downloading document:', error);
    toast.error(t('notifications.default_error'));
  }
};

// Navigate to documents list
const viewDocuments = () => {
  router.push({ name: 'documents' });
};

// Handler for document update from DocumentNavigator
const handleUpdateDocument = async () => {
  try {
    // Reload document data after update
    await loadDocumentData();
    // Save history after document update
    await saveHistory();
    toast.success(t('notifications.default_update_success'));
  } catch (error) {
    console.error('Error updating document:', error);
    toast.error(t('notifications.default_error'));
  }
};

// Handler for document deletion from DocumentNavigator
const handleDeletedDocument = () => {
  // Navigate to documents list after deletion
  router.push({ name: 'documents' });
};

// Mobile menu handlers
const handleSaveDocument = async () => {
  try {
    if (document.value && document.value.id) {
      await axios.put(`api/documents/${document.value.id}`, {
        content: document.value.content
      });
      toast.success(t('notifications.document_saved'));
      showFileMenu.value = false;
    }
  } catch (error) {
    console.error('Error saving document:', error);
    toast.error(t('notifications.document_save_error'));
  }
};

const handleDownloadDocument = async () => {
  // Check if document is fully completed before allowing download
  if (!isDocumentFullyCompleted.value) {
    toast.warning(t('download_locked_hint'));
    return;
  }

  if (document.value && (document.value.download_url || document.value.id)) {
    try {
      // Use download_url if available (includes document token), otherwise construct URL
      let downloadPath = document.value.download_url || `api/documents/${document.value.id}/download`;

      // If it's a full URL, extract just the path + query string
      if (downloadPath.startsWith('http://') || downloadPath.startsWith('https://')) {
        try {
          const urlObj = new URL(downloadPath);
          downloadPath = urlObj.pathname + urlObj.search;
        } catch (e) {
          console.warn('Failed to parse download_url, using as-is:', e);
        }
      }

      // Fetch document as blob with authentication token (added by axios interceptor)
      const response = await axios.get(downloadPath, {
        responseType: "blob",
      });

      // Create file from blob
      const file = new File([response.data], `${document.value.name || "document"}.pdf`, {
        type: "application/pdf",
      });

      // Create download link and trigger download
      const url = URL.createObjectURL(file);
      const link = window.document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.name);
      window.document.body.appendChild(link);
      link.click();
      window.document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success('Dokument został pobrany');
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Nie udało się pobrać dokumentu');
    } finally {
      showFileMenu.value = false;
    }
  }
};

const handlePrintDocument = async () => {
  // Check if document is fully completed before allowing print
  if (!isDocumentFullyCompleted.value) {
    toast.warning('Dokument nie jest jeszcze gotowy. Proszę najpierw uzupełnić wszystkie wymagane dane.');
    return;
  }

  if (!document.value?.download_url && !document.value?.id) {
    toast.error(t('documentQuestions.noDocumentToPrint'));
    return;
  }

  try {
    // Get the download URL path
    let downloadPath = document.value.download_url || `api/documents/${document.value.id}/download`;

    // If it's a full URL with wrong host, extract just the path
    if (downloadPath.startsWith('http://') || downloadPath.startsWith('https://')) {
      const urlObj = new URL(downloadPath);
      downloadPath = urlObj.pathname + urlObj.search;
    }

    // Fetch the PDF as blob with authentication
    const response = await axios.get(downloadPath, {
      responseType: 'blob',
    });

    // Create blob URL for printing
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    // Print using the blob URL (same as PLIK button)
    printJS(url);

    // Clean up after a delay
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (error) {
    console.error('Error printing:', error);
    toast.error('Nie udało się wydrukować dokumentu');
  } finally {
    showFileMenu.value = false;
  }
};

const copyLinkToClipboard = () => {
  if (document.value && document.value.negotiation_url) {
    navigator.clipboard.writeText(document.value.negotiation_url).then(() => {
      toast.success(t('notifications.link_copied'));
    }).catch(() => {
      toast.error(t('notifications.link_copy_error'));
    });
  }
};

const handleSignDocument = async () => {
  // Check if document is fully completed before allowing sign
  if (!isDocumentFullyCompleted.value) {
    toast.warning('Dokument nie jest jeszcze gotowy. Proszę najpierw uzupełnić wszystkie wymagane dane.');
    return;
  }

  if (document.value && document.value.id) {
    await uploadDocumentToDocusign(document.value.id);
  }
};

const getTemplateName = (template) => {
  if (!template) {
    // Try to get template name from document meta if available
    if (documentMeta.value && documentMeta.value.title) {
      return documentMeta.value.title;
    }
    return '';
  }

  // Handle different object structures
  if (template.template && template.template.name) {
    return template.template.name;
  }
  if (template.title) {
    return template.title;
  }
  if (template.name) {
    return template.name;
  }

  // Fallback to document meta title if template doesn't have a name
  if (documentMeta.value && documentMeta.value.title) {
    return documentMeta.value.title;
  }

  return t('documentQuestions.selectedTemplate');
};


onMounted(async () => {
  await initializeQuestions();

  // Check if user returned from cancelled payment and reopen payment dialog
  if (route.query.payment_cancelled === 'true') {
    // Remove the parameter from URL to clean it up
    const newQuery = { ...route.query };
    delete newQuery.payment_cancelled;
    router.replace({ query: newQuery });

    // Reopen payment dialog if payment is still required
    if (requiresPayment.value) {
      isPaymentDialogOpen.value = true;
    }
  }

  // Mark initial load as complete after everything is loaded
  setTimeout(() => {
    isInitialLoad.value = false;
  }, 1000);
});

onUnmounted(() => {
  // Clean up when leaving the page
  // Only reset if we're not just refreshing the same document
  if (!route.query.documentId) {
    console.log('Cleaning up document questions state on unmount');
    store.commit("editor/resetEditor");
  }
});

// Watch for route changes (like when documentId is added to URL)
watch(
  () => route.query.documentId,
  async (newDocumentId) => {
    if (newDocumentId && newDocumentId !== documentId.value) {
      // Don't reload wizard data if we're already in the editing phase
      // This prevents resetting back to questions when the route updates
      if (currentPhase.value === 'editing') {
        console.log('📍 Route changed but staying in editing phase');
        documentId.value = newDocumentId;
        store.commit("editor/updateDocumentId", newDocumentId);
        store.commit("chat/updateSessionId", newDocumentId);
        return;
      }

      documentId.value = newDocumentId;
      store.commit("editor/updateDocumentId", newDocumentId);
      store.commit("chat/updateSessionId", newDocumentId);
      await getWizardData(newDocumentId);
    }
  },
  { deep: true }
);
</script>

<style lang="scss" scoped>
.document-questions-container {
  min-height: 100vh;
  background: #FAFAFA;
  padding: 1rem 3rem 1rem;
  display: flex;
  flex-direction: column;

  // Only apply strict height constraints when in editing phase
  &.editing-phase {
    height: 100vh !important;
    max-height: 100vh !important;
    overflow: hidden !important;
  }

  @media (max-width: 1200px) {
    padding: 1rem 2rem 1rem;
  }

  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem 7rem;
    min-height: 100vh;
    overflow-x: hidden;
  }
}

.template-header {
  margin-bottom: 0.75rem;
  flex-shrink: 0;

  .template-header-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    padding: 1.25rem 2rem;
    border-radius: 16px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

    @media (max-width: 768px) {
      flex-direction: row;
      align-items: center;
      gap: 0.75rem;
      padding: 0.75rem;
      border-radius: 8px;
      text-align: left;
    }
  }

  .template-info-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    flex: 1;

    @media (max-width: 768px) {
      justify-content: center;
      text-align: center;
      flex-direction: column;
      gap: 0.75rem;
    }
  }

  .template-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.25);

    @media (max-width: 768px) {
      width: 32px;
      height: 32px;
      border-radius: 8px;
    }

    .v-icon {
      color: white;

      @media (max-width: 768px) {
        font-size: 20px;
      }
    }
  }

  .template-details-header {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;

    @media (max-width: 768px) {
      align-items: flex-start;
      text-align: left;
      gap: 0.125rem;
    }
  }

  .template-name-header {
    font-family: 'Archivo', sans-serif;
    font-weight: 700;
    font-size: 1.25rem;
    color: #1F2937;
    letter-spacing: -0.01em;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }

  .document-name-section {
    margin-top: 0.5rem;
    width: 100%;
    max-width: 400px;

    @media (max-width: 768px) {
      max-width: 280px;
    }
  }

  .document-name-input {
    :deep(.v-field__input) {
      font-size: 0.9rem;
      color: #6B7280;
      padding: 0.25rem 0;
      min-height: unset;
    }

    :deep(.v-field__underlay) {
      border-color: #E5E7EB;
    }

    :deep(.v-field--focused .v-field__underlay) {
      border-color: #3B82F6;
    }

    :deep(.v-field__input::placeholder) {
      color: #9CA3AF;
      opacity: 1;
    }

    @media (max-width: 768px) {
      :deep(.v-field__input) {
        font-size: 0.8rem;
      }
    }
  }

  .template-subtitle {
    font-family: 'Archivo', sans-serif;
    font-size: 0.875rem;
    color: #6B7280;
    font-weight: 500;

    @media (max-width: 768px) {
      font-size: 0.7rem;
    }
  }

  .back-button-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: #f8fafc;
    color: #475569;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    font-family: 'Archivo', sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.3s ease;

    @media (max-width: 768px) {
      padding: 0.4rem 0.75rem;
      font-size: 0.75rem;
      border-radius: 6px;
    }

    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
      transform: translateY(-1px);
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    }

    .v-icon {
      transition: transform 0.3s ease;
    }

    &:hover .v-icon {
      transform: translateX(-2px);
    }
  }
}

.steps-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
  padding: 0.5rem 0;
  flex-shrink: 0;
  position: relative;

  // PLIK button inline for mobile/tablet
  .mobile-plik-button-inline {
    display: none; // Hidden by default

    @media (max-width: 1024px) {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      margin-left: auto;
      margin-right: 1rem;
      padding: 0.75rem 1.5rem;
      background: #f8fafc;
      color: #475569;
      border: 1px solid #e2e8f0;
      border-radius: 12px;
      font-family: 'Archivo', sans-serif;
      font-weight: 600;
      font-size: 0.875rem;
      cursor: pointer;
      transition: all 0.3s ease;

      &:hover {
        background: #f1f5f9;
        border-color: #cbd5e1;
        transform: translateY(-1px);
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      }
    }

    @media (max-width: 768px) {
      padding: 0.4rem 0.75rem;
      font-size: 0.75rem;
      border-radius: 6px;
      margin-right: 0.5rem;
    }
  }

  .file-menu-wrapper {
    &.desktop-only {
      @media (max-width: 1024px) {
        display: none !important; // Hide desktop version on tablets/mobile
      }
    }
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    z-index: 10;

    @media (max-width: 1024px) {
      // On tablets and smaller, move to top-right corner
      position: fixed;
      top: 1rem;
      right: 1rem;
      left: auto;
      transform: none;
      z-index: 100;
    }

    @media (max-width: 768px) {
      // Keep same positioning on mobile
      position: fixed;
      top: 1rem;
      right: 1rem;
      left: auto;
      transform: none;
      z-index: 100;
    }

    :deep(.navigator) {
      display: flex;
      align-items: center;
    }

    .file-menu-debug-btn {
      background: white !important;
      color: #475569 !important;
      border: 2px solid #475569 !important;
      font-weight: 700 !important;
      padding: 0.5rem 1rem !important;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
    }

    :deep(.file-btn), :deep(.v-btn) {
      background: white !important;
      border: 1px solid #e2e8f0 !important;
      font-weight: 600;
      font-size: 0.875rem;
      padding: 0.5rem 1rem;
      border-radius: 8px;
      transition: all 0.2s ease;
      color: #475569 !important;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

      .v-icon {
        color: #475569 !important;
      }

      @media (max-width: 1024px) {
        // Tablet styles - floating button with text PLIK
        padding: 0.625rem 1rem !important;
        min-width: auto !important;
        width: auto !important;
        height: 44px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-radius: 12px !important;
        background: white !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
        border: 2px solid #475569 !important;
        font-weight: 700 !important;
        color: #475569 !important;
      }

      @media (max-width: 768px) {
        // Mobile styles - same as tablet
        padding: 0.625rem 1rem !important;
        min-width: auto !important;
        width: auto !important;
        height: 44px !important;
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        border-radius: 12px !important;
        background: white !important;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
        border: 2px solid #475569 !important;
        font-weight: 700 !important;
        color: #475569 !important;
      }

      &:hover {
        background: #f8fafc !important;
        border-color: #cbd5e1 !important;
        transform: translateY(-1px);
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
      }
    }
  }

  .steps-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    width: 100%;
    flex-wrap: wrap;

    @media (max-width: 1200px) {
      gap: 1.5rem;
    }

    @media (max-width: 1024px) {
      // On tablets, allow wrapping for PLIK button
      justify-content: space-between;
      padding: 1rem;
    }

    @media (max-width: 768px) {
      gap: 1rem;
    }
  }

  @media (max-width: 1200px) {
    margin-bottom: 1.5rem;
    padding: 0.5rem 0;
  }

  @media (max-width: 768px) {
    margin-bottom: 0.5rem;
    padding: 0.25rem 0;
    display: none; // Hide steps on mobile to save space
  }
}

.step-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;

  &.active {
    .step-text {
      color: #1E3A8A;
      font-weight: 600;
    }
  }
}

.step-number {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #E5E7EB;
  color: #9CA3AF;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 14px;
  font-family: 'Archivo', sans-serif;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  &.active {
    background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    transform: scale(1.05);

    &.pulse {
      animation: step-pulse 2s infinite;
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 0 0 0 rgba(59, 130, 246, 0.7);
    }
  }
}

@keyframes step-pulse {
  0% {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 0 0 0 rgba(59, 130, 246, 0.7);
  }
  70% {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 0 0 10px rgba(59, 130, 246, 0);
  }
  100% {
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.4), 0 0 0 0 rgba(59, 130, 246, 0);
  }
}

.step-text {
  font-family: 'Archivo', sans-serif;
  font-size: 15px;
  color: #6B7280;
  font-weight: 500;
  white-space: nowrap;
  margin-left: 0.5rem;

  @media (max-width: 640px) {
    display: none;
  }
}

.step-connector {
  width: 60px;
  height: 2px;
  background: #E5E5E5;

  @media (max-width: 768px) {
    width: 40px;
  }
}

.content-wrapper {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin: 0;
  text-align: center;
  padding: 0;

  // Only constrain height in editing phase
  .editing-phase & {
    overflow: hidden;
    min-height: 0;

    @media (max-width: 1024px) {
      // On tablets, allow scrolling
      overflow: visible !important;
    }
  }

  @media (max-width: 1024px) {
    // Tablets and smaller
    text-align: center;
    padding: 0 0.5rem;
    overflow-y: auto; // Allow vertical scrolling
    -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS
    padding-bottom: 150px; // Extra bottom padding for assistant button
  }

  @media (max-width: 768px) {
    text-align: center;
    padding: 0 0.25rem;
    overflow-y: auto; // Allow vertical scrolling
    -webkit-overflow-scrolling: touch; // Smooth scrolling on iOS
  }
}

.intro-section {
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
}


.page-title {
  font-family: 'Archivo', sans-serif;
  font-weight: 800;
  font-size: 2rem;
  line-height: 1.2;
  color: #1F2937;
  margin-bottom: 0.75rem;
  text-align: center;
  letter-spacing: -0.025em;

  @media (max-width: 768px) {
    font-size: 1.75rem;
    text-align: center;
    margin-bottom: 0.5rem;
  }
}

.description {
  font-family: 'Archivo', sans-serif;
  font-size: 1.125rem;
  line-height: 1.5;
  color: #6B7280;
  margin: 0 auto 0.75rem;
  text-align: center;
  max-width: 700px;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1rem;
    text-align: center;
    margin: 0 auto 1rem;
    line-height: 1.4;
    padding: 0 0.5rem;
  }
}

.selected-template {
  margin-bottom: 3rem;

  .template-info {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.5rem;
    background: linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%);
    padding: 1.5rem 2rem;
    border-radius: 16px;
    border: 1px solid #E2E8F0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
      transform: translateY(-1px);
    }

    @media (max-width: 768px) {
      flex-direction: column;
      align-items: flex-start;
      gap: 1rem;
      padding: 1.25rem;
    }
  }

  .template-details {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    flex: 1;
  }

  .template-icon {
    width: 40px;
    height: 40px;
    background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 8px rgba(59, 130, 246, 0.25);

    .v-icon {
      color: white;
      font-size: 20px;
    }
  }

  .template-name {
    font-family: 'Archivo', sans-serif;
    font-weight: 700;
    font-size: 1.125rem;
    color: #1F2937;
    letter-spacing: -0.01em;
  }
}

.back-to-templates-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 6px 12px;
  background: white;
  color: #64748B;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-family: 'Archivo', sans-serif;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #F8FAFC;
    color: #475569;
    border-color: #CBD5E1;
    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 4px 8px;
  }
}

.questions-section {
  margin-bottom: 2rem;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  gap: 1.5rem;
  margin: 2rem auto;
  max-width: 900px;
  width: 100%;

  .skeleton-card {
    background: white;
    border-radius: 16px;
    padding: 2rem;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border: 1px solid #E2E8F0;
    width: 100%;
    max-width: 800px;
  }

  .loading-text {
    font-family: 'Archivo', sans-serif;
    color: #374151;
    font-size: 16px;
    font-weight: 500;
    text-align: center;
    margin: 0;
  }

  @media (max-width: 768px) {
    padding: 1rem;
    margin: 1rem auto;
    max-width: 100%;

    .loading-text {
      font-size: 15px;
    }

    .skeleton-card {
      padding: 1.5rem;
      margin: 0 0.5rem;
      max-width: 100%;
    }
  }
}

// Skeleton animations and styles
@keyframes shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

// Loading phase skeleton styles
.loading-phase-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  animation: fadeIn 0.3s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  .editing-skeleton {
    display: grid;
    grid-template-columns: minmax(auto, 1fr) minmax(400px, 35%);
    gap: 1.5rem;
    width: 100%;
    flex: 1;
    height: 100%;

    @media (max-width: 1400px) {
      grid-template-columns: minmax(auto, 1fr) minmax(380px, 40%);
      gap: 1.25rem;
    }

    @media (max-width: 1200px) {
      grid-template-columns: 60% 40%;
      gap: 1rem;
    }

    @media (max-width: 1024px) {
      grid-template-columns: 1fr;
      gap: 1rem;
    }
  }

  .skeleton-document-preview {
    background: white;
    border-radius: 16px;
    border: 1px solid #E2E8F0;
    padding: 2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

    .skeleton-document-header {
      margin-bottom: 2rem;
      padding-bottom: 1rem;
      border-bottom: 2px solid #F3F4F6;

      .skeleton-line.title {
        width: 40%;
        height: 28px;
        margin-bottom: 0.75rem;
      }

      .skeleton-line.subtitle {
        width: 25%;
        height: 18px;
      }
    }

    .skeleton-document-content {
      .skeleton-paragraph {
        margin-bottom: 1.5rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  .skeleton-variables-panel {
    background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
    border-radius: 16px;
    border: 1px solid #E2E8F0;
    overflow: hidden;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);

    .skeleton-panel-header {
      background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
      padding: 1.5rem;

      .skeleton-line {
        background: linear-gradient(90deg, rgba(255,255,255,0.3) 25%, rgba(255,255,255,0.5) 50%, rgba(255,255,255,0.3) 75%) !important;
        background-size: 200px 100%;
        animation: shimmer 1.5s infinite;
      }

      .header-title {
        width: 150px;
        height: 24px;
        margin: 0 auto 0.5rem;
      }

      .header-subtitle {
        width: 250px;
        height: 16px;
        margin: 0 auto;
      }
    }

    .skeleton-panel-content {
      padding: 1.5rem;

      .skeleton-input-group {
        margin-bottom: 1.5rem;

        &:last-child {
          margin-bottom: 0;
        }

        .skeleton-label {
          width: 120px;
          height: 14px;
          margin-bottom: 0.5rem;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200px 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 4px;
        }

        .skeleton-input {
          width: 100%;
          height: 48px;
          background: linear-gradient(90deg, #f8f9fa 25%, #e9ecef 50%, #f8f9fa 75%);
          background-size: 200px 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 8px;
          border: 1px solid #E2E8F0;
        }
      }
    }
  }

  .skeleton-line {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
    height: 16px;
    margin-bottom: 0.5rem;

    &.long {
      width: 100%;
    }

    &.medium {
      width: 75%;
    }

    &.short {
      width: 50%;
      margin-bottom: 0;
    }
  }

  @media (max-width: 768px) {
    .skeleton-document-preview {
      border-radius: 0;
      border-left: none;
      border-right: none;
      padding: 1.5rem;
    }

    .skeleton-variables-panel {
      border-radius: 0;
      border-left: none;
      border-right: none;
    }
  }
}

.skeleton-progress {
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 2rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid #F1F5F9;

  .skeleton-counter {
    width: 50px;
    height: 14px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 4px;
  }

  .skeleton-bar {
    flex: 1;
    height: 6px;
    background: #F1F5F9;
    border-radius: 3px;
    overflow: hidden;

    .skeleton-bar-fill {
      width: 35%;
      height: 100%;
      background: linear-gradient(90deg, #e3f2fd 25%, #bbdefb 50%, #e3f2fd 75%);
      background-size: 200px 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 3px;
    }
  }
}

.skeleton-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: #F8FAFC;
  border-radius: 12px;

  .skeleton-icon {
    width: 48px;
    height: 48px;
    background: linear-gradient(90deg, #e3f2fd 25%, #bbdefb 50%, #e3f2fd 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
  }

  .skeleton-content {
    flex: 1;

    .skeleton-title {
      width: 180px;
      height: 18px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200px 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 4px;
      margin-bottom: 0.25rem;
    }

    .skeleton-subtitle {
      width: 140px;
      height: 14px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200px 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 4px;
    }
  }
}

.skeleton-question {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #FFFFFF;
  border: 2px solid #E2E8F0;
  border-radius: 12px;

  .skeleton-number {
    width: 24px;
    height: 24px;
    background: linear-gradient(90deg, #e3f2fd 25%, #bbdefb 50%, #e3f2fd 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 6px;
    flex-shrink: 0;
  }

  .skeleton-text {
    flex: 1;

    .skeleton-line {
      height: 16px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200px 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 4px;
      margin-bottom: 0.5rem;

      &.long { width: 100%; }
      &.medium { width: 85%; }
      &.short { width: 60%; margin-bottom: 0; }
    }
  }
}

.skeleton-options {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 2rem;

  .skeleton-option {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1rem 1.5rem;
    background: white;
    border: 2px solid #E2E8F0;
    border-radius: 12px;
    transition: all 0.2s ease;

    .skeleton-radio {
      width: 24px;
      height: 24px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200px 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 50%;
    }

    .skeleton-option-text {
      flex: 1;
      height: 15px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200px 100%;
      animation: shimmer 1.5s infinite;
      border-radius: 4px;
    }
  }
}

.skeleton-navigation {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #F1F5F9;

  .skeleton-nav-button {
    height: 36px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200px 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
    width: 80px;

    &.primary {
      background: linear-gradient(90deg, #e3f2fd 25%, #bbdefb 50%, #e3f2fd 75%);
      background-size: 200px 100%;
      animation: shimmer 1.5s infinite;
      margin-left: auto;
    }
  }
}

.error-container,
.no-questions {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  gap: 1rem;

  p {
    font-family: 'Archivo', sans-serif;
    color: #64748B;
    font-size: 16px;
    text-align: center;
  }
}

.wizard-container {
  text-align: left;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    margin-top: 0.25rem;
  }
}

.proceed-button {
  margin-top: 1rem;
  height: 56px;
  padding: 0 40px;
  background: #0023EF;
  color: white;
  border: none;
  border-radius: 100px;
  font-family: 'Archivo', sans-serif;
  font-weight: 600;
  font-size: 16px;
}

// Document Editing Section
.document-editing-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-top: 1rem;

  // Apply strict constraints only in editing phase
  .editing-phase & {
    overflow: hidden !important;
    height: 0; // Force flex item to respect parent height
    min-height: 0;
  }

  @media (max-width: 768px) {
    margin-top: 0.5rem;
  }
}

.editing-content {
  display: grid;
  grid-template-columns: minmax(auto, 1fr) minmax(400px, 35%);
  gap: 1.5rem;
  width: 100%;
  flex: 1;

  // Apply height constraints only in editing phase
  .editing-phase & {
    height: 100%;
    max-height: 100%;
    overflow: hidden;
    min-height: 0; // Important for grid items to respect container height

    @media (max-width: 1024px) {
      // On tablets and smaller, remove height constraints to allow scrolling
      height: auto !important;
      max-height: none !important;
      overflow: visible !important;
    }
  }

  @media (max-width: 1400px) {
    grid-template-columns: minmax(auto, 1fr) minmax(380px, 40%);
    gap: 1.25rem;
  }

  @media (max-width: 1200px) {
    grid-template-columns: 60% 40%;
    gap: 1rem;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 1rem;
    height: auto;
    overflow: visible;
    display: flex;
    flex-direction: column;
  }

  @media (max-width: 900px) {
    padding-bottom: 120px; // Add space for navigation buttons at bottom
  }

  @media (max-width: 768px) {
    gap: 0.5rem; // Small gap between document and input panel
    padding: 0 0.5rem; // Small horizontal padding
    margin: 0; // Ensure no margin
    display: flex;
    flex-direction: column;
    height: auto; // Let it take natural height
    overflow: visible; // Allow scrolling
  }
}

.variable-inputs-section {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  border: 1px solid #E2E8F0;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  min-height: 0;

  @media (max-width: 1024px) {
    // On tablets, remove height constraints to allow natural scrolling
    height: auto !important;
    max-height: none !important;
    min-height: auto !important;
    overflow: visible !important;
  }

  .panel-header {
    background: linear-gradient(135deg, #3B82F6 0%, #1E40AF 100%);
    padding: 1.25rem;
    border-radius: 16px 16px 0 0;
    color: white;
    flex-shrink: 0;

    .header-content {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
    }

    .header-text {
      text-align: center;
      width: 100%;

      h3 {
        font-family: 'Archivo', sans-serif;
        font-weight: 700;
        font-size: 1.25rem;
        margin: 0 0 0.5rem;
        letter-spacing: -0.01em;
        text-align: center;
      }

      p {
        font-family: 'Archivo', sans-serif;
        font-size: 0.875rem;
        margin: 0;
        padding: 0 1rem;
        opacity: 0.9;
        font-weight: 400;
        line-height: 1.4;
        text-align: center;
      }
    }

  }

  .panel-content {
    flex: 1;
    overflow-y: auto;
    padding: 1.5rem;
    min-height: 0;

    /* Custom scrollbar */
    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: #f1f5f9;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
      background: #cbd5e1;
      border-radius: 3px;

      &:hover {
        background: #94a3b8;
      }
    }
  }

  @media (max-width: 1024px) {
    .panel-header {
      padding: 1rem 1.25rem;
    }

    .panel-content {
      padding: 1.25rem;
    }
  }

  @media (max-width: 1024px) {
    // Tablets and mobile
    border-radius: 0; // Remove all border radius
    border-left: none;
    border-right: none;
    background: white; // Solid white background instead of gradient
    height: auto !important; // Remove fixed height
    max-height: none !important; // Allow natural height
    flex: 0 0 auto !important; // Don't grow or shrink
    margin-bottom: 200px !important; // Extra space for fixed navigation + assistant
    padding-bottom: 3rem !important; // Extra padding at bottom
  }

  @media (max-width: 768px) {
    // Mobile specific
    border-radius: 0; // Remove all border radius on mobile
    border-left: none;
    border-right: none;
    background: white; // Solid white background instead of gradient
    height: auto !important; // Remove fixed height
    max-height: none !important; // Allow natural height
    flex: 0 0 auto !important; // Don't grow or shrink
    margin-bottom: 100px; // Large bottom margin to ensure visibility
    padding-bottom: 2rem; // Extra padding at bottom

    .panel-header {
      padding: 0.5rem 1rem; // Reduce padding significantly
      border-radius: 0; // Remove rounded corners on mobile
      background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%); // Slightly lighter blue

      h3 {
        font-size: 1rem !important; // Smaller title
        margin: 0 0 0.25rem !important;
      }

      p {
        font-size: 0.75rem !important; // Smaller subtitle
        padding: 0 0.5rem !important;
      }
    }

    .panel-content {
      padding: 1rem;
      background: white;
      overflow: visible !important; // No scrolling in the input area
      height: auto !important; // Natural height

      // Override WizardVariables button styling on mobile
      :deep(.mobile-save-button) {
        background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%) !important;
        color: white !important;
        border-radius: 12px;
        padding: 0.875rem 1.5rem;
        font-weight: 600;
        text-transform: none;
        margin: 1.5rem auto;
        display: block;
        min-width: 200px;
        box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25);

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 6px 20px rgba(0, 35, 239, 0.35);
        }
      }

      // Style all save/next buttons consistently
      :deep(.v-btn) {
        &:not(.text):not(.outlined) {
          background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%) !important;
          color: white !important;
        }
      }
    }
  }
}

.document-preview-section {
  background: white;
  border-radius: 16px;
  border: 1px solid #E2E8F0;
  padding: 0;
  overflow: auto; // Changed from hidden to auto to allow independent scrolling
  width: 100%;
  height: 100%;
  max-height: 100%;
  min-height: 0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  position: relative; // Ensure toolbar can stick within this container

  @media (max-width: 1024px) {
    // Tablets
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    flex: 0 0 auto; // Don't grow or shrink
    height: 35vh; // Smaller height to leave room for input
    min-height: 250px; // Minimum height
    max-height: 400px; // Maximum height
    margin-bottom: 1rem;
  }

  @media (max-width: 768px) {
    // Mobile
    border-radius: 8px;
    border: 1px solid #E2E8F0;
    flex: 0 1 auto; // Don't grow, can shrink
    height: 35vh; // Fixed 35% viewport height
    min-height: 250px; // Minimum height
    max-height: 400px; // Maximum height
    margin-bottom: 0.5rem;
  }

  // Make the document scrollable within its container
  :deep(.ProseMirror) {
    width: 100% !important;
    max-width: 100% !important;
    padding: 2rem;
    font-family: 'Archivo', sans-serif;
    line-height: 1.6;
    overflow-y: auto !important;
    height: 100% !important;
    max-height: 100% !important;
    min-height: 0 !important;
    text-align: left !important; // Override parent center alignment

    @media (max-width: 768px) {
      padding: 1rem;
      font-size: 0.875rem;
      line-height: 1.5;
    }

    /* Custom scrollbar for document */
    &::-webkit-scrollbar {
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background: #f8fafc;
      border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
      background: #d1d5db;
      border-radius: 4px;

      &:hover {
        background: #9ca3af;
      }
    }
  }

  // Also ensure the Wysiwyg wrapper respects height
  :deep(.wysiwyg-container) {
    height: 100% !important;
    max-height: 100% !important;
    min-height: 0 !important;
    display: flex !important;
    flex-direction: column !important;
  }

  @media (max-width: 768px) {
    border-radius: 0;
    border: none;
    box-shadow: none;

    :deep(.ProseMirror) {
      padding: 1.5rem;
      text-align: left !important; // Override parent center alignment
    }
  }
}

.loading-variables {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  height: 100%;

  p {
    font-family: 'Archivo', sans-serif;
    color: #6B7280;
    font-size: 1rem;
    margin: 0;
    font-weight: 400;
  }
}

.variables-completed {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  height: 100%;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);

  .completion-icon {
    margin-bottom: 1.5rem;
    animation: success-bounce 0.8s ease;
    background: linear-gradient(135deg, #10B981 0%, #059669 100%);
    width: 80px;
    height: 80px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 8px 24px rgba(16, 185, 129, 0.25);

    .v-icon {
      color: white !important;
    }
  }

  h4 {
    font-family: 'Archivo', sans-serif;
    font-weight: 800;
    font-size: 2rem;
    color: #1F2937;
    margin: 0 0 1rem;
    letter-spacing: -0.02em;
  }

  p {
    font-family: 'Archivo', sans-serif;
    color: #6B7280;
    font-size: 1.125rem;
    margin: 0 0 2.5rem;
    font-weight: 400;
    line-height: 1.6;
    max-width: 380px;
  }

  .action-buttons-row {
    display: flex;
    gap: 1.5rem;
    justify-content: center;
    margin: 2rem 0;
    flex-wrap: wrap;

    .action-button {
      display: flex;
      flex-direction: column;
      align-items: center;
      cursor: pointer;
      transition: all 0.2s ease;
      text-decoration: none;

      &:hover {
        transform: translateY(-2px);

        .action-button-icon {
          background: #f8fafc;
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
        }
      }

      .action-button-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: white;
        border: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-bottom: 0.75rem;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        transition: all 0.2s ease;

        .v-icon {
          color: #64748b;
        }
      }

      span {
        font-size: 0.875rem;
        font-weight: 500;
        color: #475569;
        text-align: center;
      }
    }

    @media (max-width: 768px) {
      gap: 1rem;
      margin: 1.5rem 0;

      .action-button {
        .action-button-icon {
          width: 50px;
          height: 50px;
          margin-bottom: 0.5rem;

          .v-icon {
            font-size: 20px;
          }
        }

        span {
          font-size: 0.8125rem;
        }
      }
    }
  }

  .completion-actions {
    display: flex;
    gap: 1rem;
    flex-wrap: wrap;
    justify-content: center;
    width: 100%;
    max-width: 400px;

    .primary-action-btn {
      min-width: 180px;
      padding: 0.875rem 1.75rem;
      font-weight: 600;
      text-transform: none;
      letter-spacing: -0.01em;
      font-size: 0.9375rem;
      border-radius: 12px;
      transition: all 0.3s ease;
      background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%) !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25);

      :deep(.v-btn__prepend) {
        color: white !important;
      }

      :deep(.v-btn__content) {
        color: white !important;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 35, 239, 0.35);
      }
    }

    .secondary-action-btn {
      min-width: 180px;
      padding: 0.875rem 1.75rem;
      font-weight: 600;
      text-transform: none;
      letter-spacing: -0.01em;
      font-size: 0.9375rem;
      border-radius: 12px;
      transition: all 0.3s ease;
      background: white !important;
      color: #0023EF !important;
      border: 2px solid #E2E8F0 !important;

      :deep(.v-btn__prepend) {
        color: #0023EF !important;
      }

      :deep(.v-btn__content) {
        color: #0023EF !important;
      }

      &:hover {
        background: #F8FAFC !important;
        border-color: #0023EF !important;
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 35, 239, 0.15);
      }
    }

    // Single centered button style
    .single-action-btn {
      min-width: 200px;
      padding: 0.875rem 2rem;
      font-weight: 600;
      text-transform: none;
      letter-spacing: -0.01em;
      font-size: 0.9375rem;
      border-radius: 100px !important;
      transition: all 0.3s ease;
      background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%) !important;
      color: white !important;
      box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25);
      margin: 0 auto;

      :deep(.v-btn__prepend) {
        color: white !important;
      }

      :deep(.v-btn__content) {
        color: white !important;
      }

      // Ensure border-radius is applied to Vuetify button
      :deep(.v-btn) {
        border-radius: 100px !important;
      }

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px rgba(0, 35, 239, 0.35);
      }
    }

    @media (max-width: 480px) {
      .primary-action-btn,
      .secondary-action-btn,
      .single-action-btn {
        width: 100%;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 2rem 1.5rem;

    .completion-icon {
      width: 64px;
      height: 64px;
    }

    h4 {
      font-size: 1.5rem;
    }

    p {
      font-size: 1rem;
    }
  }
}

@keyframes success-bounce {
  0% {
    transform: scale(0);
    opacity: 0;
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes bounce {
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-8px);
  }
  60% {
    transform: translateY(-4px);
  }
}

// Payment Required Section - Professional Design
.payment-required-section {
  padding: 2rem 0;
  min-height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
    min-height: 400px;
  }
}

.payment-required-container {
  background: white;
  border-radius: 16px;
  border: 1px solid #E2E8F0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  max-width: 600px;
  width: 100%;
  overflow: hidden;

  @media (max-width: 768px) {
    border-radius: 12px;
    margin: 0 0.5rem;
  }
}

.payment-header {
  background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%);
  padding: 1.5rem 2rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1.25rem 1.5rem;
    gap: 0.75rem;
  }

  .payment-status-icon {
    width: 40px;
    height: 40px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    @media (max-width: 768px) {
      width: 36px;
      height: 36px;
      border-radius: 10px;
    }
  }

  .payment-header-text {
    flex: 1;

    h3 {
      font-family: 'Archivo', sans-serif;
      font-weight: 700;
      font-size: 1.25rem;
      color: white;
      margin: 0 0 0.25rem;
      letter-spacing: -0.01em;

      @media (max-width: 768px) {
        font-size: 1.125rem;
      }
    }

    p {
      font-family: 'Archivo', sans-serif;
      font-size: 0.875rem;
      color: rgba(255, 255, 255, 0.9);
      margin: 0;
      font-weight: 400;
    }
  }
}

.payment-content {
  padding: 2rem;
  display: flex;
  align-items: center;
  gap: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.5rem;
    flex-direction: column;
    text-align: center;
    gap: 1.25rem;
  }

  .payment-illustration {
    position: relative;
    flex-shrink: 0;

    .document-icon {
      width: 80px;
      height: 80px;
      background: #F8FAFC;
      border: 2px solid #E2E8F0;
      border-radius: 16px;
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: 768px) {
        width: 64px;
        height: 64px;
        border-radius: 12px;
      }
    }

    .lock-overlay {
      position: absolute;
      bottom: -8px;
      right: -8px;
      width: 32px;
      height: 32px;
      background: white;
      border: 2px solid #E2E8F0;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

      @media (max-width: 768px) {
        width: 28px;
        height: 28px;
        bottom: -6px;
        right: -6px;
      }
    }
  }

  .payment-info {
    flex: 1;

    h4 {
      font-family: 'Archivo', sans-serif;
      font-weight: 700;
      font-size: 1.5rem;
      color: #1F2937;
      margin: 0 0 0.75rem;
      letter-spacing: -0.01em;

      @media (max-width: 768px) {
        font-size: 1.25rem;
        margin: 0 0 0.5rem;
      }
    }

    p {
      font-family: 'Archivo', sans-serif;
      font-size: 1rem;
      color: #6B7280;
      margin: 0;
      line-height: 1.6;
      font-weight: 400;

      @media (max-width: 768px) {
        font-size: 0.9rem;
      }
    }
  }
}

.payment-actions {
  padding: 1.5rem 2rem 2rem;
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    padding: 1rem 1.5rem 1.5rem;
    flex-direction: column;
  }

  .payment-button {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.875rem 1.5rem;
    border-radius: 12px;
    font-family: 'Archivo', sans-serif;
    font-weight: 600;
    font-size: 0.875rem;
    border: none;
    cursor: pointer;
    transition: all 0.2s ease;
    text-decoration: none;
    letter-spacing: -0.01em;

    &.primary {
      background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%);
      color: white;
      box-shadow: 0 2px 8px rgba(0, 35, 239, 0.2);

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25);
      }
    }

    &.secondary {
      background: #F8FAFC;
      color: #475569;
      border: 1px solid #E2E8F0;

      &:hover {
        background: #F1F5F9;
        border-color: #CBD5E1;
        transform: translateY(-1px);
      }
    }

    @media (max-width: 768px) {
      padding: 0.75rem 1.25rem;
    }
  }
}

// Change Template Dialog Styles
.change-template-dialog {
  .dialog-title {
    font-family: 'Archivo', sans-serif;
    font-weight: 600;
    font-size: 1.25rem;
    color: #1F2937;
    padding: 1.5rem 1.5rem 0;
  }

  .dialog-content {
    font-family: 'Archivo', sans-serif;
    font-size: 1rem;
    color: #4B5563;
    line-height: 1.6;
    padding: 1rem 1.5rem;
  }

  .dialog-actions {
    padding: 0 1.5rem 1.5rem;
    gap: 0.75rem;

    .v-btn {
      font-family: 'Archivo', sans-serif;
      font-weight: 600;
      text-transform: none;
      border-radius: 12px !important;
      letter-spacing: -0.01em;
      height: auto;
      padding: 0.875rem 1.5rem;

      &[color="primary"] {
        background: linear-gradient(135deg, #0023EF 0%, #0019CC 100%) !important;
        color: white !important;
        box-shadow: 0 2px 8px rgba(0, 35, 239, 0.2);

        &:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 35, 239, 0.25);
        }
      }

      &[color="grey"] {
        background: #F8FAFC !important;
        color: #475569 !important;
        border: 1px solid #E2E8F0 !important;

        &:hover {
          background: #F1F5F9 !important;
          border-color: #CBD5E1 !important;
          transform: translateY(-1px);
        }
      }
    }
  }
}
</style>